import { useState, useEffect, useRef } from "react";

const inputStyle: React.CSSProperties = {
  backgroundColor: "#F5F0E8",
  border: "1px solid #E2D9CC",
  borderRadius: "10px",
  padding: "12px 16px",
  color: "#2C2418",
  width: "100%",
  fontFamily: "DM Sans, sans-serif",
  fontSize: "14px",
  outline: "none",
  transition: "border-color 0.2s, background-color 0.2s",
};

export default function ContactFooter() {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);
  const [error, setError] = useState("");

  const sectionRef = useRef<HTMLElement>(null);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    [leftRef.current, rightRef.current].forEach((el, i) => {
      if (!el) return;
      el.style.opacity = "0";
      el.style.transform = "translateY(28px)";
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            el.style.transition = `opacity 0.8s ease ${i * 0.15}s, transform 0.8s ease ${i * 0.15}s`;
            el.style.opacity = "1";
            el.style.transform = "translateY(0)";
            observer.disconnect();
          }
        },
        { threshold: 0.08 }
      );
      observer.observe(el);
    });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = "#C17D3C";
    e.currentTarget.style.backgroundColor = "#FFFEFB";
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.currentTarget.style.borderColor = "#E2D9CC";
    e.currentTarget.style.backgroundColor = "#F5F0E8";
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form.email || !form.message) {
      setError("Email and message are required.");
      return;
    }
    if (form.message.length > 500) {
      setError("Message must be 500 characters or fewer.");
      return;
    }
    setError("");
    setSubmitting(true);

    try {
      const body = new URLSearchParams();
      body.append("firstName", form.firstName);
      body.append("lastName", form.lastName);
      body.append("email", form.email);
      body.append("company", form.company);
      body.append("message", form.message);

      const res = await fetch("https://readdy.ai/api/form/d78a5rb2hsar0k5m07e0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      if (res.ok) {
        setForm({ firstName: "", lastName: "", email: "", company: "", message: "" });
        setSuccessMsg(true);
        setTimeout(() => setSuccessMsg(false), 5000);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section ref={sectionRef} id="contact" className="relative overflow-hidden" style={{ backgroundColor: "#FAF8F4" }}>

      {/* Top amber accent bar */}
      <div style={{ height: "3px", backgroundColor: "#C17D3C" }} />

      <div className="mx-auto px-8 lg:px-16 py-24 lg:py-32" style={{ maxWidth: "1200px" }}>

        {/* Section number + label */}
        <div className="flex items-center gap-4 mb-14">
          <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "#C17D3C" }}>04</span>
          <div style={{ width: "40px", height: "1px", backgroundColor: "#C17D3C", opacity: 0.5 }} />
          <span className="font-dm text-[11px] uppercase tracking-[0.16em]" style={{ color: "#9C8E7A" }}>Contact</span>
        </div>

        {/* 2-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">

          {/* LEFT */}
          <div ref={leftRef}>
            <h2
              className="font-lora font-semibold leading-[1.15] tracking-tight mb-6"
              style={{ fontSize: "clamp(2rem, 3.5vw, 2.8rem)", color: "#2C2418" }}
            >
              Let&apos;s build<br />something great.
            </h2>

            <p className="font-dm text-[15px] leading-[1.75] mb-10" style={{ color: "#6B5F4E", maxWidth: "380px" }}>
              Open to full-time opportunities where I can lead at the intersection of product and engineering. Based in Boise, ID — open to remote.
            </p>

            {/* Links */}
            <div className="flex flex-col gap-4 mb-12">
              <a
                href="https://www.linkedin.com/in/josue-tello"
                rel="nofollow"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div
                  className="flex items-center justify-center flex-shrink-0 transition-all duration-200"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#F0EBE1",
                    border: "1px solid #E2D9CC",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "#C17D3C"; (e.currentTarget as HTMLDivElement).style.borderColor = "#C17D3C"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "#F0EBE1"; (e.currentTarget as HTMLDivElement).style.borderColor = "#E2D9CC"; }}
                >
                  <i className="ri-linkedin-fill text-[#6B5F4E] text-base group-hover:text-white transition-colors duration-200" />
                </div>
                <span className="font-dm text-[14px] text-[#6B5F4E] group-hover:text-[#C17D3C] transition-colors duration-200">
                  linkedin.com/in/josue-tello
                </span>
              </a>

              <a
                href="https://github.com/tellojsu"
                rel="nofollow"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <div
                  className="flex items-center justify-center flex-shrink-0 transition-all duration-200"
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    backgroundColor: "#F0EBE1",
                    border: "1px solid #E2D9CC",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "#C17D3C"; (e.currentTarget as HTMLDivElement).style.borderColor = "#C17D3C"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = "#F0EBE1"; (e.currentTarget as HTMLDivElement).style.borderColor = "#E2D9CC"; }}
                >
                  <i className="ri-github-fill text-[#6B5F4E] text-base group-hover:text-white transition-colors duration-200" />
                </div>
                <span className="font-dm text-[14px] text-[#6B5F4E] group-hover:text-[#C17D3C] transition-colors duration-200">
                  github.com/tellojsu
                </span>
              </a>
            </div>

            {/* Availability badge */}
            <div
              className="inline-flex items-center gap-3 rounded-full px-5 py-3"
              style={{ backgroundColor: "#F0EBE1", border: "1px solid #E2D9CC" }}
            >
              <span
                className="rounded-full flex-shrink-0"
                style={{ width: "8px", height: "8px", backgroundColor: "#4A6741", boxShadow: "0 0 0 3px rgba(74,103,65,0.15)" }}
              />
              <span className="font-dm text-[13px]" style={{ color: "#6B5F4E" }}>
                Open to full-time opportunities
              </span>
            </div>
          </div>

          {/* RIGHT: Form */}
          <div ref={rightRef}>
            <form
              data-readdy-form
              id="contact-josue-tello"
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              {/* Row 1: First + Last */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-dm text-[12px] uppercase tracking-[0.08em] text-[#9C8E7A] mb-2">First name</label>
                  <input type="text" name="firstName" value={form.firstName} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Josue" style={inputStyle} />
                </div>
                <div>
                  <label className="block font-dm text-[12px] uppercase tracking-[0.08em] text-[#9C8E7A] mb-2">Last name</label>
                  <input type="text" name="lastName" value={form.lastName} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Tello" style={inputStyle} />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block font-dm text-[12px] uppercase tracking-[0.08em] text-[#9C8E7A] mb-2">
                  Email <span style={{ color: "#C17D3C" }}>*</span>
                </label>
                <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="you@company.com" required style={inputStyle} />
              </div>

              {/* Company */}
              <div>
                <label className="block font-dm text-[12px] uppercase tracking-[0.08em] text-[#9C8E7A] mb-2">
                  Company <span style={{ color: "#C4B49A", fontSize: "11px", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                </label>
                <input type="text" name="company" value={form.company} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Acme Corp" style={inputStyle} />
              </div>

              {/* Message */}
              <div>
                <label className="block font-dm text-[12px] uppercase tracking-[0.08em] text-[#9C8E7A] mb-2">
                  Message <span style={{ color: "#C17D3C" }}>*</span>
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  placeholder="Tell me about the role or what you're building..."
                  maxLength={500}
                  required
                  style={{ ...inputStyle, minHeight: "130px", resize: "vertical" }}
                />
                <p className="font-dm text-[11px] text-right mt-1" style={{ color: "#C4B49A" }}>
                  {form.message.length}/500
                </p>
              </div>

              {error && <p className="font-dm text-[13px] text-red-500">{error}</p>}

              <div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 font-dm font-medium text-[14px] text-[#FAF8F4] rounded-full transition-all duration-200 hover:-translate-y-px disabled:opacity-60 whitespace-nowrap cursor-pointer"
                  style={{ backgroundColor: "#2C2418", padding: "13px 28px" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#C17D3C"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = "#2C2418"; }}
                >
                  <i className="ri-send-plane-line text-sm" />
                  {submitting ? "Sending..." : "Send message"}
                </button>

                {successMsg && (
                  <p className="font-dm text-[13px] text-[#4A6741] mt-3">
                    Thanks! I&apos;ll be in touch soon.
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #E2D9CC" }}>
        <div className="mx-auto px-8 lg:px-16 py-6" style={{ maxWidth: "1200px" }}>
          <p className="font-dm text-[12px] text-center" style={{ color: "#C4B49A" }}>
            &copy; 2026 Josue Tello &middot; Boise, ID &middot; Open to remote
          </p>
        </div>
      </div>
    </section>
  );
}
